/**
 * Dev Sessions at a glance (Gordon 2026-11-02: "round numbers off, with click
 * slide down more details, toggle close"). Pure: sessions in, the four card
 * numbers and what each card's panel shows out. Used by services/dev-session
 * (summary) and components/dashboard/dev-glance (cards + panels).
 */

export interface DevGlanceSession {
  startMs: number;
  seconds: number;
  type: string;
  person: string;
  costEur: number;
  keystrokes: number;
  mouse: number;
  description?: string;
  aiTokens?: number;
  aiCostEur?: number;
}

export interface DevGlanceGroup { key: string; sessions: number; hours: number; costEur: number }
export interface DevGlanceTop { startMs: number; description?: string; count: number }

export interface DevGlance {
  sessions: number;
  hours: number;
  costEur: number;
  keystrokes: number;
  mouse: number;
  avgMinutes: number;
  longest: { minutes: number; startMs: number; description?: string } | null;
  firstMs: number | null;
  lastMs: number | null;
  byType: DevGlanceGroup[];
  byPerson: DevGlanceGroup[];
  /** Chronological, `YYYY-MM` keys (UTC). */
  byMonth: DevGlanceGroup[];
  keysPerHour: number;
  mousePerHour: number;
  keysPerSession: number;
  mousePerSession: number;
  topKeys: DevGlanceTop[];
  topMouse: DevGlanceTop[];
  aiTokens: number;
  aiCostEur: number;
}

const monthKey = (ms: number) => new Date(ms).toISOString().slice(0, 7);

function group(sessions: DevGlanceSession[], keyOf: (s: DevGlanceSession) => string): DevGlanceGroup[] {
  const map = new Map<string, DevGlanceGroup>();
  for (const s of sessions) {
    const key = keyOf(s) || '—';
    const g = map.get(key) ?? { key, sessions: 0, hours: 0, costEur: 0 };
    g.sessions += 1;
    g.hours += s.seconds / 3600;
    g.costEur += s.costEur;
    map.set(key, g);
  }
  return [...map.values()];
}

function top(sessions: DevGlanceSession[], count: (s: DevGlanceSession) => number, n = 3): DevGlanceTop[] {
  return sessions
    .filter(s => count(s) > 0)
    .sort((a, b) => count(b) - count(a))
    .slice(0, n)
    .map(s => ({ startMs: s.startMs, description: s.description, count: count(s) }));
}

export function summariseDevSessions(sessions: DevGlanceSession[]): DevGlance {
  const n = sessions.length;
  const seconds = sessions.reduce((a, s) => a + s.seconds, 0);
  const hours = seconds / 3600;
  const keystrokes = sessions.reduce((a, s) => a + s.keystrokes, 0);
  const mouse = sessions.reduce((a, s) => a + s.mouse, 0);
  const longestS = sessions.reduce<DevGlanceSession | null>((a, s) => (!a || s.seconds > a.seconds ? s : a), null);
  const starts = sessions.map(s => s.startMs).filter(ms => ms > 0);
  return {
    sessions: n,
    hours,
    costEur: sessions.reduce((a, s) => a + s.costEur, 0),
    keystrokes,
    mouse,
    avgMinutes: n ? seconds / n / 60 : 0,
    longest: longestS ? { minutes: longestS.seconds / 60, startMs: longestS.startMs, description: longestS.description } : null,
    firstMs: starts.length ? Math.min(...starts) : null,
    lastMs: starts.length ? Math.max(...starts) : null,
    byType: group(sessions, s => s.type).sort((a, b) => b.hours - a.hours),
    byPerson: group(sessions, s => s.person).sort((a, b) => b.hours - a.hours).slice(0, 8),
    byMonth: group(sessions.filter(s => s.startMs > 0), s => monthKey(s.startMs)).sort((a, b) => a.key.localeCompare(b.key)),
    keysPerHour: hours > 0 ? keystrokes / hours : 0,
    mousePerHour: hours > 0 ? mouse / hours : 0,
    keysPerSession: n ? keystrokes / n : 0,
    mousePerSession: n ? mouse / n : 0,
    topKeys: top(sessions, s => s.keystrokes),
    topMouse: top(sessions, s => s.mouse),
    aiTokens: sessions.reduce((a, s) => a + (s.aiTokens || 0), 0),
    aiCostEur: sessions.reduce((a, s) => a + (s.aiCostEur || 0), 0),
  };
}
